import { type ServiceInquiryButton_productizedService$key } from '#app/__generated__/relay/ServiceInquiryButton_productizedService.graphql.js';
import { Button } from '#app/components/Buttons/Button.js';
import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { ButtonVariant } from '#app/components/Buttons/types.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useOnboardingRedirection } from '#app/components/RedirectToOnboardingModal/useOnboardingRedirection.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { MailSendOutlineIcon } from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, useFragment } from '@contra/relay';
import { useCallback, useMemo } from 'react';
import { styled } from 'styled-components';

const StyledButton = styled(Button)`
  span {
    white-space: nowrap;
  }
`;

type ServiceInquiryButtonProps = {
  readonly block?: boolean;
  readonly buttonVariant?: ButtonVariant;
  readonly isIconOnly?: boolean;
  readonly minWidth?: number;
  readonly productizedServiceRef: ServiceInquiryButton_productizedService$key;
  readonly section: string;
};

let hasBeenViewed = false;
const logViewOnce = (callback: VoidFunction) => {
  if (!hasBeenViewed) {
    hasBeenViewed = true;
    // eslint-disable-next-line n/callback-return
    callback();
  }
};

export const ServiceInquiryButton = ({
  block,
  buttonVariant = 'V2Gradient',
  isIconOnly = false,
  minWidth,
  productizedServiceRef,
  section,
}: ServiceInquiryButtonProps) => {
  const service = useFragment<ServiceInquiryButton_productizedService$key>(
    graphql`
      fragment ServiceInquiryButton_productizedService on ProductizedService {
        id
        analyticsId
        price {
          type
        }
        productizedServiceType
        userProfile {
          id
          analyticsId
          generalInquiryCtaIsVisible
          displayUsername
          ...DiscoverUnauthenticatedInquiryModal_profile
        }
      }
    `,
    productizedServiceRef,
  );
  const serviceAnalyticsProperties = useMemo(
    () => ({
      service_id: service.analyticsId,
      service_pricing_type:
        service.price.type === '%future added value'
          ? undefined
          : service.price.type,
      service_type:
        service.productizedServiceType === '%future added value'
          ? undefined
          : (service.productizedServiceType ?? undefined),
    }),
    [service.analyticsId, service.price.type, service.productizedServiceType],
  );

  const { track } = useAnalytics();
  const currentUser = useCurrentUser();
  const location = useLocation();
  const { openModal } = useModal({
    identifier: 'ServiceInquiryModal',
    modalProps: {
      contractorUserProfileId: service.userProfile.id,
      productizedServiceId: service.id,
    },
    staticAdditionalTrackingProperties: serviceAnalyticsProperties,
  });
  const isViewingOwnProfile =
    service.userProfile.id === currentUser?.profile.id;
  const {
    handleOnboardingRedirectionModalOpen:
      handleClientOnboardingRedirectionModalOpen,
    shouldRedirectUser: shouldRedirectUserToOnboardClient,
  } = useOnboardingRedirection({
    overrideUserType: 'client',
    redirectTo: location.path,
  });

  const { openModal: openUnauthenticatedInquiryModal } = useModal({
    identifier: 'DiscoverUnauthenticatedInquiryModal',
    modalProps: {
      userProfileRef: service.userProfile,
    },
    staticAdditionalTrackingProperties: serviceAnalyticsProperties,
  });

  const handleClick = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'demand_service_inquiry',
      section,
      ...serviceAnalyticsProperties,
    });

    if (
      !currentUser ||
      (!currentUser.userGroups.includes('CLIENT_ONBOARDING_COMPLETE') &&
        currentUser.selectedUserType === 'CLIENT')
    ) {
      openUnauthenticatedInquiryModal();
      return;
    }

    if (shouldRedirectUserToOnboardClient) {
      handleClientOnboardingRedirectionModalOpen();
      return;
    }

    openModal();
  }, [
    currentUser,
    handleClientOnboardingRedirectionModalOpen,
    openModal,
    openUnauthenticatedInquiryModal,
    section,
    serviceAnalyticsProperties,
    shouldRedirectUserToOnboardClient,
    track,
  ]);
  const contactCta = 'Contact me';
  const disableButton =
    !service.userProfile.generalInquiryCtaIsVisible || isViewingOwnProfile;

  const requestButton = isIconOnly ? (
    <CircularIconButton
      aria-label={contactCta}
      disabled={disableButton}
      icon={MailSendOutlineIcon}
      onClick={handleClick}
      size="medium"
      variant={buttonVariant}
    />
  ) : (
    <StyledButton
      aria-label={contactCta}
      block={block}
      disabled={disableButton}
      icon={MailSendOutlineIcon}
      iconPlacement="left"
      minWidth={minWidth}
      onClick={handleClick}
      size="medium"
      variant={buttonVariant}
    >
      {contactCta}
    </StyledButton>
  );

  useMount(() => {
    logViewOnce(() => {
      // this is being wrapped because this button is used twice on the page but we want to log the event only once per page load
      track('button', {
        action: 'viewed',
        button_name: 'demand_service_inquiry',
        ...serviceAnalyticsProperties,
      });
    });
  });

  if (service.userProfile.generalInquiryCtaIsVisible) {
    return requestButton;
  }

  return (
    <Tooltip
      content="This freelancer is not accepting requests at this time. Check back later!"
      hideReferenceContainerOutlineOnFocus
    >
      {requestButton}
    </Tooltip>
  );
};
